import { Client, Company, User } from 'src/constants/dataTypes';
import { setExtraData } from 'src/store/entities/extraDataSlice';
import { FileChannel } from 'src/store/files/types';

import { ApiMethods, ApiTags, appAPI } from '.';

// New FileChannel type matching the backend API response from /file-channels endpoint
// This is the structure returned by the new optimized endpoint
export interface FileChannelApiResponse {
  id: string;
  object: string; // "fileChannel"
  identityId: string;
  createdAt: string;
  updatedAt: string;
  membershipType: 'individual' | 'company' | 'group';
  rootClientPermissions?: 'read_only' | 'read_write';
  membershipEntityId?: string; // The ID of the entity (company or client) this channel is based on
  memberIds: string[]; // Array of member user IDs
  companyId?: string; // The company ID for all channel types
  // Notification fields
  lastNotificationTimestamp?: string; // ISO timestamp of last notification
  lastNotificationSenderId?: string; // User ID of notification sender
}

export interface GetCompanyFileChannelsParams {
  companyId: string;
}

export interface GetCompanyFileChannelsResponse {
  fileChannels: FileChannel[];
}

export interface GetClientFileChannelsParams {
  clientId: string;
}

export interface GetClientFileChannelsResponse {
  fileChannels: FileChannel[];
}

export interface ListFileChannelsParams {
  companyId?: string;
  clientId?: string;
  membershipType?: string;
  limit?: number;
  nextToken?: string;
}

// extraData is organized by type for easier consumption
export interface ExtraDataResponse {
  clients: Record<string, Client>; // CLIENT_USER objects
  users: Record<string, User>; // INTERNAL_USER objects
  companies: Record<string, Company>; // Company objects
}

export interface ListFileChannelsResponse {
  data: FileChannelApiResponse[];
  nextToken?: string;
  extraData?: ExtraDataResponse;
}

export interface GetExtraDataParams {
  clientIds?: string[]; // Explicitly fetch only client users
  companyIds?: string[]; // Explicitly fetch only companies
  memberIds?: string[]; // Fetch both CLIENT_USER and INTERNAL_USER in parallel (unknown type)
}

// GetExtraDataResponse is the same as ExtraDataResponse (backend returns it directly)
export type GetExtraDataResponse = ExtraDataResponse;

export const fileChannelsApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    getCompanyFileChannels: build.query<
      GetCompanyFileChannelsResponse,
      GetCompanyFileChannelsParams
    >({
      query: ({ companyId }) => ({
        path: `/entities/FILE_CHANNEL`,
        method: ApiMethods.get,
        options: {
          queryStringParameters: {
            companyId,
          },
        },
      }),
      providesTags: [ApiTags.fileChannels],
    }),
    getClientFileChannels: build.query<
      GetClientFileChannelsResponse,
      GetClientFileChannelsParams
    >({
      query: ({ clientId }) => ({
        path: `/entities/FILE_CHANNEL`,
        method: ApiMethods.get,
        options: {
          queryStringParameters: {
            clientId,
          },
        },
      }),
      providesTags: [ApiTags.fileChannels],
    }),
    listFileChannels: build.query<
      ListFileChannelsResponse,
      ListFileChannelsParams & { includeExtraData?: boolean }
    >({
      query: (params) => {
        const queryParams: Record<string, string | boolean> = {};
        queryParams.limit = (params.limit || 100).toString();
        // Only include extraData if explicitly requested (default: false for subsequent pages)
        if (params.includeExtraData !== undefined) {
          queryParams.includeExtraData = params.includeExtraData;
        }
        if (params.nextToken) queryParams.nextToken = params.nextToken;

        return {
          path: `/file-channels`,
          method: ApiMethods.get,
          options: {
            queryStringParameters: queryParams,
          },
        };
      },
      providesTags: [ApiTags.fileChannels],
      transformResponse: (response: {
        data?: FileChannelApiResponse[];
        nextToken?: string;
        extraData?: ExtraDataResponse;
      }) => ({
        data: response.data || [],
        nextToken: response.nextToken,
        extraData: response.extraData,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const result = await queryFulfilled;
          // Dispatch extraData to Redux store when query succeeds
          // extraData is already organized by type from the backend
          if (result.data.extraData) {
            dispatch(
              setExtraData({
                clients: result.data.extraData.clients || {},
                users: result.data.extraData.users || {},
                companies: result.data.extraData.companies || {},
              }),
            );
          }
        } catch {
          // Silently fail - the query error will be handled by RTK Query
          // The error is already handled by RTK Query's error handling
        }
      },
    }),
    getExtraData: build.query<GetExtraDataResponse, GetExtraDataParams>({
      query: (params) => {
        const queryParams: Record<string, string> = {};
        if (params.clientIds && params.clientIds.length > 0) {
          queryParams.clientIds = params.clientIds.join(',');
        }
        if (params.companyIds && params.companyIds.length > 0) {
          queryParams.companyIds = params.companyIds.join(',');
        }
        if (params.memberIds && params.memberIds.length > 0) {
          queryParams.memberIds = params.memberIds.join(',');
        }

        return {
          path: `/file-channels/extra-data`,
          method: ApiMethods.get,
          options: {
            queryStringParameters: queryParams,
          },
        };
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const result = await queryFulfilled;
          // Dispatch extraData to Redux store when query succeeds
          // extraData is already organized by type from the backend
          if (result.data) {
            dispatch(
              setExtraData({
                clients: result.data.clients || {},
                users: result.data.users || {},
                companies: result.data.companies || {},
              }),
            );
          }
        } catch {
          // Silently fail - the query error will be handled by RTK Query
        }
      },
    }),
  }),
});

export const {
  useGetCompanyFileChannelsQuery,
  useGetClientFileChannelsQuery,
  useListFileChannelsQuery,
  useGetExtraDataQuery,
} = fileChannelsApi;
